import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BarChart3, Calendar as CalendarIcon, TrendingUp, Package, AlertTriangle, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [reportType, setReportType] = useState('sales');
  const [salesReport, setSalesReport] = useState(null);
  const [productsReport, setProductsReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    fetchReports();
  }, [reportType]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      if (reportType === 'sales') {
        const params = {};
        if (dateRange?.from) {
          const from = dateRange.from;
          params.start_date = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}T00:00:00`;
        }
        if (dateRange?.to) {
          const to = dateRange.to;
          params.end_date = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}T23:59:59`;
        }
        const response = await axios.get(`${API_URL}/api/reports/sales`, { params });
        setSalesReport(response.data);
      } else {
        const response = await axios.get(`${API_URL}/api/reports/products`);
        setProductsReport(response.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const COLORS = ['#2563EB', '#06B6D4', '#10B981', '#F59E0B', '#F43F5E', '#8B5CF6'];

  const paymentMethodLabels = {
    dinheiro: 'Dinheiro',
    pix: 'PIX',
    pix_cnpj: 'PIX - CNPJ',
    cartao_credito: 'Cartão Crédito',
    cartao_debito: 'Cartão Débito',
    crediario_bemol: 'Crediário Bemol',
    vale_mercadoria: 'Vale Mercadoria',
    avancard: 'Avancard',
    boleto: 'Boleto',
    multiplo: 'Múltiplo'
  };

  const renderSalesReport = () => {
    if (!salesReport) return null;

    const paymentData = Object.entries(salesReport.by_payment_method || {}).map(([key, value]) => ({
      name: paymentMethodLabels[key] || key,
      value: value
    }));

    const sellerData = Object.entries(salesReport.by_seller || {}).map(([key, value]) => ({
      name: key,
      value: value
    }));

    const dailyData = (salesReport.daily_totals || []).slice(-14).map(item => ({
      name: item.date ? format(new Date(item.date + 'T12:00:00'), 'dd/MM', { locale: ptBR }) : '',
      total: item.total
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total de Vendas</p>
                  <p className="text-3xl font-bold text-blue-600">{formatCurrency(salesReport.total_sales)}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <TrendingUp className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Quantidade de Vendas</p>
                  <p className="text-3xl font-bold text-emerald-600">{salesReport.count}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <BarChart3 className="text-emerald-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Ticket Médio</p>
                  <p className="text-3xl font-bold text-violet-600">
                    {formatCurrency(salesReport.count > 0 ? salesReport.total_sales / salesReport.count : 0)}
                  </p>
                </div>
                <div className="p-3 bg-violet-100 rounded-lg">
                  <TrendingUp className="text-violet-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Sales */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Vendas por Dia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `R$${v}`} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Total']}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Por Forma de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* By Seller */}
        {sellerData.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Vendas por Vendedor</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-right">Total Vendido</TableHead>
                    <TableHead className="text-right">% do Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellerData.map((seller, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{seller.name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(seller.value)}</TableCell>
                      <TableCell className="text-right">
                        {((seller.value / salesReport.total_sales) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderProductsReport = () => {
    if (!productsReport) return null;

    const categoryData = Object.entries(productsReport.by_category || {}).map(([key, value]) => ({
      name: key,
      count: value.count,
      value: value.value
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total de Produtos</p>
                  <p className="text-3xl font-bold text-blue-600">{productsReport.total_products}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Valor em Estoque</p>
                  <p className="text-3xl font-bold text-emerald-600">{formatCurrency(productsReport.total_stock_value)}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <TrendingUp className="text-emerald-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Estoque Baixo</p>
                  <p className="text-3xl font-bold text-amber-600">{productsReport.low_stock_products?.length || 0}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <AlertTriangle className="text-amber-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* By Category */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Produtos por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={12} width={120} />
                  <Tooltip 
                    formatter={(value, name) => [name === 'count' ? value : formatCurrency(value), name === 'count' ? 'Qtd' : 'Valor']}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="count" fill="#2563EB" name="Quantidade" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock Products */}
        {productsReport.low_stock_products?.length > 0 && (
          <Card className="border border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="text-amber-600" size={20} />
                Produtos com Estoque Baixo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-center">Estoque Atual</TableHead>
                    <TableHead className="text-center">Estoque Mínimo</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productsReport.low_stock_products.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-sm">{p.sku}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.category || '-'}</TableCell>
                      <TableCell className="text-center font-bold text-amber-600">{p.stock}</TableCell>
                      <TableCell className="text-center">{p.min_stock}</TableCell>
                      <TableCell>
                        <Badge className={p.stock === 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                          {p.stock === 0 ? 'Sem estoque' : 'Repor'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Relatórios</h1>
          <p className="text-slate-500 mt-1">Análise detalhada do seu negócio</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="space-y-2">
              <Label>Tipo de Relatório</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="w-48" data-testid="report-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Vendas</SelectItem>
                  <SelectItem value="products">Produtos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reportType === 'sales' && (
              <div className="space-y-2">
                <Label>Período</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-64 justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange?.from ? (
                        dateRange?.to ? (
                          <>
                            {format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR })} - {format(dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}
                          </>
                        ) : (
                          format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR })
                        )
                      ) : (
                        'Selecione o período'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={dateRange?.from || new Date()}
                      selected={dateRange}
                      onSelect={(range) => setDateRange(range || { from: null, to: null })}
                      numberOfMonths={2}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="flex items-end">
              <Button onClick={fetchReports} className="bg-blue-600 hover:bg-blue-700">
                Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : (
        <>
          {reportType === 'sales' && renderSalesReport()}
          {reportType === 'products' && renderProductsReport()}
        </>
      )}
    </div>
  );
};

export default Reports;
